import { useState } from "react";
import { useNavigate } from 'react-router-dom';
import { storage } from "@/lib/storage";
import toast from "react-hot-toast";
import {
  Users,
  TrendingUp,
  DollarSign,
  AlertCircle,
  LogOut,
  ChevronDown,
  Package,
  Globe,
  Briefcase,
  FileText,
} from "lucide-react";
import { StatCard, StatGrid } from "../shared/StatCard";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useDispatch } from 'react-redux';
import { updateUserData } from "@/lib/store/slices/UserSlice";
import { setRefreshTokenCookie } from "@/lib/clientCookie";
import { logoutService } from "@/services/auth";
import { clearPermissions } from "@/lib/store/slices/PermissionsSlice";

export default function SuperuserDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'overview' | 'subscriptions' | 'zones' | 'employees' | 'reports'>('overview');
  const userDetails = useCurrentUser();
  const dispatch = useDispatch();
  let isLoggingOut = false;

  const handleLogout = async () => {
    if (isLoggingOut) return;
    isLoggingOut = true;

    try {
      const refreshToken = storage.getRefreshToken();

      if (refreshToken) {
        await logoutService(refreshToken);
      }
    } catch (err) {
      console.error("Logout API failed:", err);
    } finally {
      storage.setRefreshToken('');
      setRefreshTokenCookie('');
      storage.logout();
      dispatch(updateUserData(null));
      dispatch(clearPermissions());

      toast.success("Logged out successfully");
     navigate("/login", { replace: true });

      isLoggingOut = false;
    }
  };

  // Mock data - replace with actual API calls
  const stats = {
    totalSubscriptions: 0,
    activeNow: 0,
    monthlyRevenue: 0,
    expiringSoon: 0,
    zones: 4,
    branches: 2,
    employees: 0,
    pendingVerification: 0,
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-blue-50 to-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40 shadow-sm">
        <div className="max-w-full mx-auto px-6 py-4 flex items-center justify-between">
          {/* Logo & Brand */}
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 relative rounded-lg overflow-hidden shadow-md border border-gray-200 bg-white">
              <img src="/mlblogo.jpeg" alt="Mera Lunch Box Logo" className="w-full h-full object-cover" />
            </div>
            <h1 className="text-2xl font-extrabold text-gray-900 tracking-tight">Mera Lunch Box</h1>
          </div>

          {/* Navigation */}
          <nav className="hidden md:flex items-center gap-8 bg-white px-4 py-2">
            <button
              onClick={() => navigate("/admin-dashboard")}
              className="font-semibold text-base text-gray-600 hover:text-brand-700 hover:bg-gray-50 px-4 py-2 rounded-lg transition-all"
            >
              Dashboard
            </button>
            <button
              onClick={() => navigate("/users")}
              className="font-semibold text-base text-gray-600 hover:text-brand-700 hover:bg-gray-50 px-4 py-2 rounded-lg transition-all"
            >
              Users
            </button>
            <button
              onClick={() => navigate("/subscriptions")}
              className="font-semibold text-base text-gray-600 hover:text-brand-700 hover:bg-gray-50 px-4 py-2 rounded-lg transition-all"
            >
              Subscriptions
            </button>
            <button
              onClick={() => navigate("/admin-dashboard")}
              className="font-semibold text-base text-gray-600 hover:text-brand-700 hover:bg-gray-50 px-4 py-2 rounded-lg transition-all"
            >
              Masters
            </button>
            <button
              onClick={() => navigate("/reports")}
              className="font-semibold text-base text-gray-600 hover:text-brand-700 hover:bg-gray-50 px-4 py-2 rounded-lg transition-all"
            >
              Reports
            </button>
          </nav>

          {/* Right Section */}
          <div className="flex items-center gap-4">
            <button className="relative p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
              <FileText className="w-5 h-5" />
              <span className="absolute top-0 right-0 w-2 h-2 bg-red-500 rounded-full" />
            </button>

            {/* User Menu */}
            <div className="relative group">
              <button className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors">
                <div className="w-8 h-8 bg-brand-100 rounded-full flex items-center justify-center text-sm font-semibold text-brand-500">
                  {userDetails?.username?.[0]?.toUpperCase() || 'S'}
                </div>
                <span className="text-sm text-gray-900 font-medium hidden sm:inline">
                  {userDetails?.username || 'Super Admin'}
                </span>
                <ChevronDown className="w-4 h-4 text-gray-600" />
              </button>

              {/* Dropdown Menu */}
              <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                <div className="p-4 border-b border-gray-200">
                  <p className="text-sm font-semibold text-gray-900">{userDetails?.username || 'Super Admin'}</p>
                  <p className="text-xs text-gray-600">{userDetails?.email || 'superadmin@meralunchbox.com'}</p>
                </div>
                <div className="p-2">
                  <button
                    type="button"
                    onClick={handleLogout}
                    className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Logout
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-full mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Page Header with Tabs */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 tracking-tight">Super User Dashboard</h2>
              <p className="text-gray-600 mt-1">System-wide insights & full administrative control</p>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="flex items-center gap-1 sm:gap-4 border-b border-gray-200 overflow-x-auto scrollbar-none">
            <button
              onClick={() => setActiveTab('overview')}
              className={`pb-3 px-2 font-semibold text-sm transition-all ${activeTab === 'overview'
                  ? 'text-brand-500 border-b-2 border-brand-500'
                  : 'text-gray-600 hover:text-brand-500'
                }`}
            >
              Overview
            </button>
            <button
              onClick={() => setActiveTab('subscriptions')}
              className={`pb-3 px-2 font-semibold text-sm transition-all ${activeTab === 'subscriptions'
                  ? 'text-brand-500 border-b-2 border-brand-500'
                  : 'text-gray-600 hover:text-brand-500'
                }`}
            >
              Subscriptions
            </button>
            <button
              onClick={() => setActiveTab('zones')}
              className={`pb-3 px-2 font-semibold text-sm transition-all ${activeTab === 'zones'
                  ? 'text-brand-500 border-b-2 border-brand-500'
                  : 'text-gray-600 hover:text-brand-500'
                }`}
            >
              Zones
            </button>
            <button
              onClick={() => setActiveTab('employees')}
              className={`pb-3 px-2 font-semibold text-sm transition-all ${activeTab === 'employees'
                  ? 'text-brand-500 border-b-2 border-brand-500'
                  : 'text-gray-600 hover:text-brand-500'
                }`}
            >
              Employees
            </button>
            <button
              onClick={() => setActiveTab('reports')}
              className={`pb-3 px-2 font-semibold text-sm transition-all ${activeTab === 'reports'
                  ? 'text-brand-500 border-b-2 border-brand-500'
                  : 'text-gray-600 hover:text-brand-500'
                }`}
            >
              Reports
            </button>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Grid */}
            <div className="mb-8">
              <StatGrid>
                <StatCard
                  title="Total Subscriptions"
                  value={stats.totalSubscriptions}
                  icon={<Users className="w-6 h-6" />}
                  variant="primary"
                  trend={{ value: 12, isPositive: true, label: "this month" }}
                />
                <StatCard
                  title="Active Now"
                  value={stats.activeNow}
                  icon={<TrendingUp className="w-6 h-6" />}
                  variant="success"
                />
                <StatCard
                  title="Monthly Revenue"
                  value={`₹${stats.monthlyRevenue}`}
                  icon={<DollarSign className="w-6 h-6" />}
                  variant="info"
                />
                <StatCard
                  title="Expiring Soon"
                  value={stats.expiringSoon}
                  icon={<AlertCircle className="w-6 h-6" />}
                  variant="warning"
                />
              </StatGrid>
            </div>

            {/* Secondary Stats */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-brand-100 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5 text-brand-500" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Zones</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.zones}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <Briefcase className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Branches</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.branches}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                    <Users className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Employees</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.employees}</p>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-sm text-gray-600">Pending Verification</p>
                    <p className="text-2xl font-bold text-gray-900">{stats.pendingVerification}</p>
                  </div>
                </div>
              </div>
            </div>

            {/* System Status */}
            <div className="mb-8">
              <h3 className="text-xl font-bold text-gray-900 mb-4">System Status</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center gap-2 mb-2">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse" />
                    <p className="text-sm font-semibold text-gray-900">System Status</p>
                  </div>
                  <p className="text-sm text-gray-600">All systems operational</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Last Sync</p>
                  <p className="text-sm text-gray-600">Just now</p>
                </div>

                <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <p className="text-sm font-semibold text-gray-900 mb-2">Active Users</p>
                  <p className="text-sm text-gray-600">5 online</p>
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div>
              <h3 className="text-xl font-bold text-gray-900 mb-4">Quick Actions</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <button
                  onClick={() => navigate("/admin-dashboard")}
                  className="bg-brand-500 hover:bg-brand-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <Globe className="w-5 h-5" />
                  + Add Zone
                </button>

                <button
                  onClick={() => navigate("/admin-dashboard")}
                  className="bg-brand-500 hover:bg-brand-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <Briefcase className="w-5 h-5" />
                  + Add Branch
                </button>

                <button
                  onClick={() => navigate("/admin-dashboard")}
                  className="bg-brand-500 hover:bg-brand-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <Users className="w-5 h-5" />
                  + Add Employee
                </button>

                <button
                  onClick={() => setActiveTab('reports')}
                  className="bg-green-600 hover:bg-green-700 text-white font-semibold py-3 px-6 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                >
                  <FileText className="w-5 h-5" />
                  View Reports
                </button>
              </div>
            </div>
          </>
        )}

        {/* Subscriptions Tab */}
        {activeTab === 'subscriptions' && (
          <div className="space-y-6">
            {/* Filters */}
            <div className="flex items-center gap-4">
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400">
                <option>All Zones</option>
                <option>North Zone</option>
                <option>South Zone</option>
                <option>East Zone</option>
                <option>West Zone</option>
              </select>

              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-400">
                <option>All Status</option>
                <option>Active</option>
                <option>Expired</option>
                <option>Pending</option>
              </select>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Child Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Parent</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Zone</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Status</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Expiry Date</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan={6} className="py-12 text-center text-gray-500">
                        No subscriptions found
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Zones Tab */}
        {activeTab === 'zones' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Zones Management</h3>
              <button
                onClick={() => navigate("/admin-dashboard")}
                className="bg-brand-500 hover:bg-brand-700 text-white font-semibold py-2 px-4 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <Globe className="w-4 h-4" />
                + Add New Zone
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {['North Zone', 'South Zone', 'East Zone', 'West Zone'].map((zone) => (
                <div key={zone} className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="text-lg font-bold text-gray-900">{zone}</h4>
                    <div className="flex gap-2">
                      <button className="text-brand-500 hover:text-brand-800 text-sm font-medium">Edit</button>
                      <button className="text-red-600 hover:text-red-800 text-sm font-medium">Delete</button>
                    </div>
                  </div>
                  <div className="flex gap-6 text-sm text-gray-600">
                    <div>
                      <span className="font-medium">Branches:</span> {zone === 'North Zone' ? 2 : 0}
                    </div>
                    <div>
                      <span className="font-medium">Subscriptions:</span> 0
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Employees Tab */}
        {activeTab === 'employees' && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-bold text-gray-900">Employees Management</h3>
              <button
                onClick={() => navigate("/admin-dashboard")}
                className="bg-brand-500 hover:bg-brand-700 text-white font-semibold py-2 px-4 rounded-lg transition-all shadow-md hover:shadow-lg flex items-center gap-2"
              >
                <Users className="w-4 h-4" />
                + Add New Employee
              </button>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b border-gray-200">
                    <tr>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Name</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Email</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Role</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Branch</th>
                      <th className="text-left py-3 px-4 font-semibold text-sm text-gray-900">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td colSpan={5} className="py-12 text-center text-gray-500">
                        No employees found. Use the "Add New Employee" button to get started.
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Reports Tab */}
        {activeTab === 'reports' && (
          <div className="space-y-6">
            <h3 className="text-xl font-bold text-gray-900">Monthly Reports</h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Revenue Report</h4>
                <p className="text-sm text-gray-600">Monthly & yearly revenue breakdown</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Subscription Analytics</h4>
                <p className="text-sm text-gray-600">Growth & retention metrics</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Employee Performance</h4>
                <p className="text-sm text-gray-600">Zone-wise performance review</p>
              </div>

              <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow cursor-pointer">
                <h4 className="text-lg font-bold text-gray-900 mb-2">Delivery Metrics</h4>
                <p className="text-sm text-gray-600">Delivery boy performance & routes</p>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
